import { useEffect, useRef } from 'react';

let bodyScrollLockCount = 0;
let lockedScrollY = 0;
let activeViewportHandler = null;
let savedStyles = null;

const getScrollbarWidth = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
};

/**
 * Hook that locks body scroll when active.
 * Prevents scroll chaining and rubber-band effects on mobile.
 *
 * @param {boolean} isLocked - Se o scroll deve estar bloqueado
 */
export function useLockBodyScroll(isLocked) {
  const isActiveRef = useRef(false);

  useEffect(() => {
    if (!isLocked) {
      return undefined;
    }

    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;
    if (!body || !documentElement || isActiveRef.current) {
      return undefined;
    }

    if (bodyScrollLockCount === 0) {
      savedStyles = {
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
        bodyBoxSizing: body.style.boxSizing,
        bodyPaddingRight: body.style.paddingRight,
        htmlOverflow: documentElement.style.overflow,
        htmlOverscrollBehavior: documentElement.style.overscrollBehavior,
        bodyOverscrollBehavior: body.style.overscrollBehavior,
      };

      lockedScrollY = window.scrollY;

      activeViewportHandler = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };

      activeViewportHandler();
      window.addEventListener('resize', activeViewportHandler);
      window.addEventListener('orientationchange', activeViewportHandler);
    }

    bodyScrollLockCount += 1;
    isActiveRef.current = true;

    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${lockedScrollY}px`;
    body.style.width = '100%';
    body.style.boxSizing = 'border-box';
    if (bodyScrollLockCount === 1) {
      const scrollbarWidth = getScrollbarWidth();
      if (scrollbarWidth > 0) {
        const currentPaddingRight = Number.parseFloat(
          window.getComputedStyle(body).paddingRight
        ) || 0;
        body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
      }
    }
    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';

    return () => {
      if (!isActiveRef.current) return;

      isActiveRef.current = false;
      bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);

      if (bodyScrollLockCount > 0) {
        return;
      }

      const originalStyles = savedStyles || {};
      body.style.overflow = originalStyles.bodyOverflow || '';
      body.style.position = originalStyles.bodyPosition || '';
      body.style.top = originalStyles.bodyTop || '';
      body.style.width = originalStyles.bodyWidth || '';
      body.style.boxSizing = originalStyles.bodyBoxSizing || '';
      body.style.paddingRight = originalStyles.bodyPaddingRight || '';
      documentElement.style.overflow = originalStyles.htmlOverflow || '';
      documentElement.style.overscrollBehavior =
        originalStyles.htmlOverscrollBehavior || '';
      body.style.overscrollBehavior =
        originalStyles.bodyOverscrollBehavior || '';

      const isJsdom =
        typeof window.navigator?.userAgent === 'string' &&
        /jsdom/i.test(window.navigator.userAgent);

      if (!isJsdom && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo(0, lockedScrollY);
        } catch (_) {
          // ignore environments where scroll restoration is unavailable
        }
      }

      if (activeViewportHandler) {
        window.removeEventListener('resize', activeViewportHandler);
        window.removeEventListener('orientationchange', activeViewportHandler);
        activeViewportHandler = null;
      }

      document.documentElement.style.removeProperty('--vh');
      savedStyles = null;
    };
  }, [isLocked]);
}

export default useLockBodyScroll;
